import { Controller } from "@hotwired/stimulus"
import {
  providerMatchesFilters,
  visibleProviderIndexes,
} from "../lib/provider_search_filters.mjs"

const PLAN_ACCEPTANCE_HIDDEN_APPEARANCES = ["yellow", "red"]

function planAcceptanceFilterActive(hiddenAppearances) {
  return PLAN_ACCEPTANCE_HIDDEN_APPEARANCES.every((appearance) => hiddenAppearances.includes(appearance))
}

// The new pages' drawer bar: "50 results", or "Showing 42 of 50 results" while filters hide some
// (worded as ProvidersHelper#provider_search_results_count_text renders it first).
export function resultsCountText(visible, total) {
  const noun = total === 1 ? "result" : "results"
  return visible >= total ? `${total} ${noun}` : `Showing ${visible} of ${total} ${noun}`
}

// Global-admin preview controls for provider-search result visibility.
// Quick chips (Active / Recommended) sit above the results; the detailed
// controls live in a bottom sheet that slides up over the results.
export default class extends Controller {
  static targets = [
    "activeCheckbox",
    "activeChip",
    "appearanceCheckbox",
    "feedbackWidget",
    "hiddenCount",
    "hiddenNotice",
    "planAcceptanceChip",
    "providerCard",
    "resultsCount",
    "sheet",
    "sheetOverlay",
    "sheetTrigger",
    "sexFilter",
    "showAllLink",
  ]

  static values = {
    activeByDefault: Boolean,
    hiddenAppearancesByDefault: Array,
    providers: Array,
  }

  connect() {
    this.activeOnly = this.activeByDefaultValue
    this.hiddenAppearances = [...this.hiddenAppearancesByDefaultValue]
    this.sex = ""
    this.closeTimeout = null
    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.sheetOpen()) this.closeSheet()
    }
    document.addEventListener("keydown", this.keydownHandler)
    this.syncInputs()
    this.applyFilters()
    this.initialized = true
  }

  // Lazily loaded result chunks connect their cards after the initial render;
  // re-apply the current filters so they arrive with the right visibility.
  providerCardTargetConnected() {
    if (!this.initialized) return
    this.applyFilters()
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
  }

  toggleActive(event) {
    event?.preventDefault?.()
    this.activeOnly = !this.activeOnly
    this.syncInputs()
    this.applyFilters()
  }

  togglePlanAcceptance(event) {
    event?.preventDefault?.()
    this.hiddenAppearances = planAcceptanceFilterActive(this.hiddenAppearances)
      ? this.hiddenAppearances.filter((appearance) => !PLAN_ACCEPTANCE_HIDDEN_APPEARANCES.includes(appearance))
      : [...new Set([...this.hiddenAppearances, ...PLAN_ACCEPTANCE_HIDDEN_APPEARANCES])]
    this.syncInputs()
    this.applyFilters()
  }

  updateFromCheckboxes() {
    if (this.hasActiveCheckboxTarget) this.activeOnly = this.activeCheckboxTarget.checked
    this.hiddenAppearances = this.appearanceCheckboxTargets
      .filter((checkbox) => !checkbox.checked)
      .map((checkbox) => checkbox.value)
    this.syncInputs()
    this.applyFilters()
  }

  updateSex(event) {
    if (event?.target?.type !== "radio") return
    this.sex = event.target.value || ""
    this.applyFilters()
  }

  // Clears every filter: the compact "Show N hidden" action, or the new pages' "Show all". Show all
  // hides itself once nothing is hidden, so a keyboard user who pressed it lands on the count instead
  // of losing focus to the page.
  showAll(event) {
    event?.preventDefault?.()
    const focusWasOnShowAll = this.hasShowAllLinkTarget && this.showAllLinkTarget.contains?.(globalThis.document?.activeElement)
    this.activeOnly = false
    this.hiddenAppearances = []
    this.sex = ""
    this.syncInputs()
    this.applyFilters()
    if (focusWasOnShowAll && this.hasResultsCountTarget) this.resultsCountTarget.focus()
  }

  openSheet(event) {
    event?.preventDefault?.()
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = null
    this.previouslyFocused = event?.currentTarget || document.activeElement
    this.sheetTriggerTarget.setAttribute("aria-expanded", "true")
    this.sheetOverlayTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sheetTarget.classList.remove("translate-y-full")
        // preventScroll: the sheet is still transformed off the bottom edge for the
        // 300ms slide, so a scrolling focus drags the whole drawer body down with it.
        this.sheetTarget.querySelector("button, input, select, textarea, [href], [tabindex]:not([tabindex='-1'])")?.focus({ preventScroll: true })
      })
    })
  }

  closeSheet(event) {
    event?.preventDefault?.()
    this.sheetTarget.classList.add("translate-y-full")
    this.sheetTriggerTarget.setAttribute("aria-expanded", "false")
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => {
      this.sheetOverlayTarget.classList.add("hidden")
      this.closeTimeout = null
    }, 300)
    if (this.previouslyFocused?.focus) this.previouslyFocused.focus({ preventScroll: true })
    this.previouslyFocused = null
  }

  sheetOpen() {
    return this.hasSheetOverlayTarget && !this.sheetOverlayTarget.classList.contains("hidden")
  }

  applyFilters() {
    const filters = { activeOnly: this.activeOnly, hiddenAppearances: this.hiddenAppearances, sex: this.sex || "" }
    let visibleIndex = 0
    const visibleCards = []

    this.providerCardTargets.forEach((card) => {
      const visible = providerMatchesFilters(card.dataset, filters)
      card.classList.toggle("hidden", !visible)
      card.setAttribute("aria-hidden", String(!visible))

      if (visible) {
        visibleIndex += 1
        visibleCards.push(card)
        const number = card.querySelector("[data-provider-result-number]")
        if (number) number.textContent = String(visibleIndex)
      }
    })

    if (this.hasFeedbackWidgetTarget) {
      const feedbackAnchor = visibleCards[Math.min(visibleCards.length, 2) - 1]
      this.feedbackWidgetTarget.classList.toggle("hidden", !feedbackAnchor)
      this.feedbackWidgetTarget.setAttribute("aria-hidden", String(!feedbackAnchor))
      feedbackAnchor?.after(this.feedbackWidgetTarget)
    }

    // Prefer the full result-set metadata (which includes providers whose
    // cards have not been lazily loaded yet) so map markers stay in sync.
    const providerData = this.providersValue?.length > 0
      ? this.providersValue
      : this.providerCardTargets.map((card) => card.dataset)
    const visibleIndexes = visibleProviderIndexes(providerData, filters)
    const hiddenCount = providerData.length - visibleIndexes.length
    if (this.hasHiddenNoticeTarget) {
      this.hiddenNoticeTarget.classList.toggle("hidden", hiddenCount === 0)
      this.hiddenNoticeTarget.setAttribute("aria-label", `Show ${hiddenCount} hidden ${hiddenCount === 1 ? "provider" : "providers"} by clearing filters`)
      if (this.hasHiddenCountTarget) this.hiddenCountTarget.textContent = String(hiddenCount)
    }
    if (this.hasResultsCountTarget) {
      this.resultsCountTarget.textContent = resultsCountText(visibleIndexes.length, providerData.length)
    }
    if (this.hasShowAllLinkTarget) this.showAllLinkTarget.classList.toggle("hidden", hiddenCount === 0)
    this.dispatch("updated", {
      detail: {
        visibleProviderIndexes: visibleIndexes,
      },
    })
  }

  syncInputs() {
    if (this.hasActiveCheckboxTarget) this.activeCheckboxTarget.checked = this.activeOnly
    this.appearanceCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = !this.hiddenAppearances.includes(checkbox.value)
    })
    if (this.hasSexFilterTarget) {
      const sex = this.sex || ""
      this.sexFilterTarget.querySelectorAll("input[type=radio]").forEach((radio) => {
        radio.checked = radio.value === sex
      })
      // segmented-radio-motion only repositions its highlight on change events.
      this.sexFilterTarget.querySelector(".segmented-radio")?.dispatchEvent(new Event("change"))
    }
    if (this.hasActiveChipTarget) {
      this.activeChipTarget.setAttribute("aria-pressed", String(this.activeOnly))
    }
    if (this.hasPlanAcceptanceChipTarget) {
      this.planAcceptanceChipTarget.setAttribute("aria-pressed", String(planAcceptanceFilterActive(this.hiddenAppearances)))
    }
  }
}
